import { formatCompactMxn } from '../lib/format';
import type { AgingBucket } from '../api/types';

interface Props {
  buckets: AgingBucket[];
}

// SAT-conventional aging colors: current = green; 31-60 = yellow; 61-90 =
// orange (warning); 90+ = red (collections concern).
const COLOR: Record<string, string> = {
  '0-30':  'bg-emerald-500',
  '31-60': 'bg-amber-400',
  '61-90': 'bg-orange-500',
  '90+':   'bg-red-600',
};
const LABEL_COLOR: Record<string, string> = {
  '0-30':  'text-emerald-700 dark:text-emerald-400',
  '31-60': 'text-amber-700 dark:text-amber-400',
  '61-90': 'text-orange-700 dark:text-orange-400',
  '90+':   'text-red-700 dark:text-red-400',
};

/**
 * Horizontal stacked bar + four labels under it. Replaces the cramped
 * "0-30: $X · 31-60: $Y · …" single-line text on the AR KPI tile. Reading
 * order: visual weight (how much red?) first, then exact numbers below.
 *
 * Zero-total edge case (no AR at all) renders an empty bar with a hint —
 * still useful so the layout doesn't jump when AR drops to zero.
 */
export default function AgingMiniBar({ buckets }: Props) {
  const total = buckets.reduce((s, b) => s + b.amount_mxn, 0);
  return (
    <div className="mt-2 space-y-1.5">
      <div className="h-2 rounded-full bg-tremor-background-muted overflow-hidden flex">
        {total === 0 ? (
          <div className="flex-1" />
        ) : (
          buckets.map((b) => {
            const pct = (b.amount_mxn / total) * 100;
            if (pct === 0) return null;
            return (
              <div
                key={b.label}
                className={COLOR[b.label] ?? 'bg-tremor-content'}
                style={{ width: pct + '%' }}
                title={`${b.label} días: ${formatCompactMxn(b.amount_mxn)}`}
              />
            );
          })
        )}
      </div>
      <div className="grid grid-cols-4 gap-1 text-xs">
        {buckets.map((b) => (
          <div key={b.label} className="leading-tight">
            <div className={`font-medium ${LABEL_COLOR[b.label] ?? ''}`}>{b.label}</div>
            <div className="text-tremor-content tabular-nums">
              {formatCompactMxn(b.amount_mxn)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
